/**
 * PositionsList - 포지션 목록 컴포넌트
 *
 * CRITICAL: 절대 목업 데이터 사용 금지!
 * - 데이터 없으면: "보유 포지션이 없습니다" 명확히 표시
 * - API 오류 시: "API 오류: {상세 메시지}" 표시
 * - 로딩 중: "로딩 중..." 스피너 표시
 * - 실제 운영 API 데이터만 사용
 */
import React, { useCallback, useEffect, useState } from 'react';
import { fetchPortfolio } from '../services/portfolioApi';
import { PositionInfo } from '../types/portfolio';
import { formatCurrency, formatPercent } from '../utils/formatNumber';
import './PositionsList.css';

const PositionsList: React.FC = () => {
  const [positions, setPositions] = useState<PositionInfo[] | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const loadPositions = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await fetchPortfolio();
      setPositions(data.positions);
    } catch (err) {
      setError(err instanceof Error ? err.message : '알 수 없는 오류');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadPositions();
  }, [loadPositions]);

  // 실제 데이터만 표시
  if (loading && positions === null) {
    return (
      <div className="positions-list">
        <h3>보유 포지션 <button onClick={loadPositions} className="refresh-btn">새로고침</button></h3>
        <div className="loading">
          <div className="spinner"></div>
          <p>로딩 중...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="positions-list">
        <h3>보유 포지션 <button onClick={loadPositions} className="refresh-btn">새로고침</button></h3>
        <div className="error">
          <p>API 오류: {error}</p>
          <button onClick={loadPositions}>다시 시도</button>
        </div>
      </div>
    );
  }

  if (!positions || positions.length === 0) {
    return (
      <div className="positions-list">
        <h3>보유 포지션 (0개) <button onClick={loadPositions} className="refresh-btn">새로고침</button></h3>
        <div className="empty">
          <p>보유 포지션이 없습니다</p>
        </div>
      </div>
    );
  }

  return (
    <div className="positions-list">
      <h3>보유 포지션 ({positions.length}개) <button onClick={loadPositions} className="refresh-btn">새로고침</button></h3>

      <div className="table-wrapper">
        <table className="positions-table">
          <thead>
            <tr>
              <th className="align-left">종목</th>
              <th className="align-right">수량</th>
              <th className="align-right">현재가</th>
              <th className="align-right">평가액</th>
              <th className="align-right">미실현 손익</th>
              <th className="align-right">수익률</th>
            </tr>
          </thead>
          <tbody>
            {positions.map((pos) => {
              const currentPrice = pos.market_value / pos.qty;
              const isProfit = pos.unrealized_pl_percent >= 0;

              return (
                <tr key={pos.symbol}>
                  <td className="symbol">{pos.symbol}</td>
                  <td className="qty">
                    {formatQuantity(pos.qty)}
                  </td>
                  <td className="price">{formatCurrency(currentPrice)}</td>
                  <td className="value">{formatCurrency(pos.market_value)}</td>
                  <td className={isProfit ? 'profit' : 'loss'}>
                    {isProfit ? '+' : ''}{formatCurrency(pos.unrealized_pl)}
                  </td>
                  <td className={isProfit ? 'profit' : 'loss'}>
                    {formatPercent(pos.unrealized_pl_percent)}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
};

function formatQuantity(value: number): string {
  return value.toLocaleString('ko-KR', { maximumFractionDigits: 6 });
}

export default PositionsList;
